import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, MAT_DATE_LOCALE, DateAdapter } from '@angular/material/core';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxCurrencyDirective } from 'ngx-currency';

import { HeaderComponent } from '../../../shared/components/header/header.component';
import { FooterActionComponent } from '../../../shared/components/footer-action/footer-action.component';
import { LoadingBoxComponent } from '../../../shared/components/loading-box/loading-box.component';
import { PageScrollSpyComponent, ScrollSpySection } from '../../../shared/components/page-scroll-spy/page-scroll-spy.component';
import { SelectPesquisavelComponent } from '../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { AlertCardComponent } from '../../../shared/components/alert-card/alert-card.component';
import { ContasPagarCentroCustoComponent } from '../contas-pagar-cadastro/components/contas-pagar-centro-custo/contas-pagar-centro-custo.component';

import { ModuloEnum } from '../../../shared/enums/usuario/modulo.enum';
import { TipoContaEnum } from '../../../shared/enums/financas/tipo-conta.enum';
import { ContaFinanceira } from '../../../shared/services/models/financas/conta-financeira.model';
import { MatDatepicker } from '@angular/material/datepicker';
import { ContasPagarUtil } from '@shared/helpers/contas-pagar.util';
import { FuncionalidadeEnum } from '../../../shared/enums/usuario/funcionalidade.enum';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { ContasPagarParcelaService } from '../../../shared/services/apis/financas/contas-pagar-parcela.service';
import { ContasPagarService } from '../../../shared/services/apis/financas/contas-pagar.service';
import { MaskService } from '../../../shared/services/mask.service';
import { AbntPipe } from '../../../shared/pipes/abnt.pipe';
import { CustomDateAdapter } from '../../../shared/services/custom-date-adapter';
import { MatDialog } from '@angular/material/dialog';
import { normalizarValorOrdenacao } from '../../../shared/helpers/ordenacao-tabela.helper';

export interface ContasPagarParcelaAnexoStruct {
    idContasPagarParcelaAnexo?: number;
    nomeArquivo: string;
    tamanhoBytes: number;
    urlArquivo?: string;
    base64?: string;
}

@Component({
    selector: 'app-contas-pagar-parcela-cadastro',
    standalone: true,
    providers: [
        { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
        { provide: DateAdapter, useClass: CustomDateAdapter },
    ],
    imports: [
        CommonModule,
        ReactiveFormsModule,
        RouterModule,
        MatButtonModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatFormFieldModule,
        MatInputModule,
        MatDatepickerModule,
        MatNativeDateModule,
        MatSlideToggleModule,
        MatTooltipModule,
        NgxCurrencyDirective,
        HeaderComponent,
        FooterActionComponent,
        LoadingBoxComponent,
        PageScrollSpyComponent,
        SelectPesquisavelComponent,
        AlertCardComponent,
        AbntPipe,
        ContasPagarCentroCustoComponent
    ],
    templateUrl: './contas-pagar-parcela-cadastro.component.html',
    styleUrl: './contas-pagar-parcela-cadastro.component.css'
})
export class ContasPagarParcelaCadastroComponent implements OnInit {

    public moduloEnum = ModuloEnum.Financas;
    public funcionalidadeEnum = FuncionalidadeEnum.Financas_Contas_Pagar;

    public form!: FormGroup;
    public isLoading: boolean = false;
    public isLoadingLookups: boolean = false;
    public isSaving: boolean = false;

    public idContasPagarParcela: number | null = null;
    public currencyMask: any;
    public percentageMask: any;

    // Dados do documento pai (readonly, exibição no header)
    public idContasPagar: number = 0;
    public nomeFornecedor: string = '';
    public descricaoOriginal: string = '';
    public valorTotalDocumento: number = 0;
    public qtdParcelas: number = 0;
    public nroParcela: number | null = null;
    public idCategoriaFinanceira: number = 0;
    public idPessoaFornecedor: number = 0;
    public statusDescricao: string | null = null;

    // Controle de edição
    public podeEditarVencimento: boolean = true;

    // Liquidações
    public liquidacoes: any[] = [];

    // Anexos da parcela
    public anexos: ContasPagarParcelaAnexoStruct[] = [];
    public readonly MAX_FILE_SIZE = 2 * 1024 * 1024;
    public readonly MAX_TOTAL_SIZE = 10 * 1024 * 1024;
    public isHovering: boolean = false;

    // Lookups
    public listaFormaPagamento: any[] = [];
    public listaContaFinanceira: any[] = [];
    public listaTags: any[] = [];
    public listaCentrosCusto: any[] = [];
    public listaGridConfiguracoesRateio: any[] = [];

    // Scroll Spy
    get sections(): ScrollSpySection[] {
        const s: ScrollSpySection[] = [
            { id: 'dados-parcela', label: 'Dados da Parcela' },
            { id: 'centro-custo', label: 'Centro de Custo' },
            { id: 'previsao', label: 'Previsão de Pagamento' },
            { id: 'complementos', label: 'Complementos' }
        ];
        if (this.liquidacoes.length > 0) {
            s.splice(2, 0, { id: 'resumo-baixa', label: 'Resumo da Baixa' });
        }
        return s;
    }

    get idFormaPagamentoControl(): FormControl {
        return this.form.get('idFormaPagamento') as FormControl;
    }

    // Exibição condicional de "Código de Barras" (só Boleto) e "Chave PIX" (só PIX), espelhando
    // contas-pagar-complementares.component.ts. A forma de pagamento vem de um lookup livre (sem
    // enum fixo), então a checagem é por texto da descrição.
    private get descricaoFormaPagamentoSelecionada(): string | null {
        const id = this.idFormaPagamentoControl.value;
        if (!id) return null;
        const forma = this.listaFormaPagamento.find((f: any) => f.idFormaPagamento === id);
        return forma?.descricao ?? null;
    }

    get mostrarCodigoBarras(): boolean {
        const desc = this.descricaoFormaPagamentoSelecionada;
        return !!desc && (normalizarValorOrdenacao(desc) as string).includes('boleto');
    }

    get mostrarChavePix(): boolean {
        const desc = this.descricaoFormaPagamentoSelecionada;
        return !!desc && (normalizarValorOrdenacao(desc) as string).includes('pix');
    }

    get idContaFinanceiraControl(): FormControl {
        return this.form.get('idContaFinanceira') as FormControl;
    }

    get tagsControl(): FormControl {
        return this.form.get('tags') as FormControl;
    }

    get contaSelecionada(): ContaFinanceira | null {
        const id = this.idContaFinanceiraControl?.value;
        if (!id) return null;
        return (this.listaContaFinanceira as ContaFinanceira[]).find(c => c.idContaFinanceira === id) ?? null;
    }

    get isContaCartao(): boolean {
        return this.contaSelecionada?.idTipoConta === TipoContaEnum.CartaoCredito;
    }

    get diaFechamentoCartao(): number | null {
        return this.contaSelecionada?.diaFechamentoCartao ?? null;
    }

    get diaVencimentoCartao(): number | null {
        return this.contaSelecionada?.diaVencimentoCartao ?? null;
    }

    /**
     * Forma de pagamento não é um enum fixo (lookup livre por empresa), então a
     * busca por "Cartão de crédito" é por texto — cuidado para não casar com "Cartão de débito".
     */
    private get formaPagamentoCartaoCredito(): any | null {
        return this.listaFormaPagamento.find((f: any) => {
            const desc = normalizarValorOrdenacao(f.descricao) as string;
            return desc.includes('cartao') && desc.includes('credito');
        }) ?? null;
    }

    /**
     * Conta cartão só paga por cartão de crédito — trava a forma de pagamento
     * quando a conta selecionada é do tipo Cartão, reverte quando deixa de ser.
     */
    private aplicarRegraFormaPagamentoCartao(): void {
        if (this.isContaCartao) {
            const formaCartao = this.formaPagamentoCartaoCredito;
            if (formaCartao) {
                this.idFormaPagamentoControl.setValue(formaCartao.idFormaPagamento, { emitEvent: false });
                this.idFormaPagamentoControl.disable({ emitEvent: false });
            }
        } else {
            this.idFormaPagamentoControl.enable({ emitEvent: false });
        }
    }

    public cartaoDateFilter = (d: Date | null): boolean => {
        if (!d || !this.isContaCartao) return true;
        const fechamento = this.diaFechamentoCartao;
        const vencimento = this.diaVencimentoCartao;
        if (fechamento == null || vencimento == null) return true;
        const mesMinimo = ContasPagarUtil.calcularMesFaturaDestino(new Date(), fechamento, vencimento);
        return new Date(d.getFullYear(), d.getMonth(), 1) >= mesMinimo;
    };

    constructor(
        private fb: FormBuilder,
        private route: ActivatedRoute,
        private router: Router,
        private contasPagarParcelaService: ContasPagarParcelaService,
        private contasPagarService: ContasPagarService,
        private alertService: AlertService,
        private maskService: MaskService,
        private dialog: MatDialog
    ) {
        this.currencyMask = { ...this.maskService.getCurrencyMask(), allowNegative: false };
        this.percentageMask = { ...this.maskService.getDecimalMask2(), allowNegative: false };
    }

    ngOnInit(): void {
        this.initForm();
        this.carregarLookups();
        this.checkRouteParams();

        this.form.get('dataVencimento')?.valueChanges.subscribe((data) => {
            if (!this.isContaCartao || !data) return;
            const dia = this.diaVencimentoCartao;
            if (dia == null) return;
            const dataAjustada = ContasPagarUtil.ajustarParaDiaVencimento(new Date(data), dia);
            if (new Date(data).getTime() !== dataAjustada.getTime()) {
                this.form.get('dataVencimento')?.setValue(dataAjustada, { emitEvent: false });
            }
        });

        this.form.get('idContaFinanceira')?.valueChanges.subscribe(() => {
            this.aplicarRegraFormaPagamentoCartao();

            if (!this.isContaCartao) return;
            const dia = this.diaVencimentoCartao;
            if (dia == null) return;
            const dataAtual = this.form.get('dataVencimento')?.value;
            if (!dataAtual) return;
            const dataAjustada = ContasPagarUtil.ajustarParaDiaVencimento(new Date(dataAtual), dia);
            this.form.get('dataVencimento')?.setValue(dataAjustada, { emitEvent: false });
        });
    }

    private initForm(): void {
        this.form = this.fb.group({
            valor: [null, [Validators.required, Validators.min(0.01)]],
            dataVencimento: ['', Validators.required],
            datetimePrevisao: [''],
            descricao: [''],
            idFormaPagamento: [null],
            idContaFinanceira: [null],
            valorJurosPrevisto: [null],
            valorMultaPrevisto: [null],
            valorDescontoPrevisto: [null],
            valorAcrescimoPrevisto: [null],
            observacoesHtml: [''],
            codigoBarrasLinhaDigitavel: [''],
            chavePix: [''],
            tags: [[]],

            // Rateio por centro de custo — pertence ao documento pai, exposto aqui pra edição.
            // Estrutura (dadosGerais.isRateio/idCentroCusto + rateio.itens) segue o mesmo formato
            // esperado por <app-contas-pagar-centro-custo>, reaproveitado do cadastro do documento.
            dadosGerais: this.fb.group({
                isRateio: [false],
                idCentroCusto: [null]
            }),
            rateio: this.fb.group({
                itens: this.fb.array([])
            })
        });
    }

    private carregarLookups(): void {
        this.isLoadingLookups = true;
        this.contasPagarService.getLookups().subscribe({
            next: (res) => {
                this.isLoadingLookups = false;
                if (!res.isError) {
                    this.listaFormaPagamento = res.formasPagamento || [];
                    this.listaContaFinanceira = res.contasFinanceiras || [];
                    this.listaTags = res.tags || [];
                    this.listaCentrosCusto = res.centrosCusto || [];
                    this.listaGridConfiguracoesRateio = res.rateiosCentroCusto || [];
                }
            },
            error: () => {
                this.isLoadingLookups = false;
                this.alertService.show('Erro', 'Não foi possível carregar as listas de seleção.', AlertType.error);
            }
        });
    }

    get rotaVoltar(): any[] {
        if (this.idContasPagar) {
            return ['/financas/contas-pagar/cadastrar', { idContasPagar: this.idContasPagar }];
        }
        return ['/financas/contas-pagar'];
    }

    private checkRouteParams(): void {
        const paramId = this.route.snapshot.paramMap.get('idContasPagarParcela');
        const queryIdContasPagar = this.route.snapshot.queryParamMap.get('idContasPagar');
        if (queryIdContasPagar) {
            this.idContasPagar = Number(queryIdContasPagar);
        }
        if (paramId) {
            this.idContasPagarParcela = Number(paramId);
            this.loadData();
        } else {
            this.alertService.show('Erro', 'Parcela não informada.', AlertType.error);
            this.router.navigate(['/financas/contas-pagar']);
        }
    }

    private loadData(): void {
        if (!this.idContasPagarParcela) return;

        this.isLoading = true;
        this.contasPagarParcelaService.getById(this.idContasPagarParcela).subscribe({
            next: (res: any) => {
                this.isLoading = false;
                if (res.isError) {
                    this.alertService.show('Erro', res.errorDescription || 'Erro ao carregar a parcela.', AlertType.error);
                    this.router.navigate(['/financas/contas-pagar']);
                    return;
                }

                // Dados do documento pai
                this.idContasPagar = res.idContasPagar;
                this.idPessoaFornecedor = res.idPessoaFornecedor;
                this.nomeFornecedor = res.nomeFornecedor || '';
                this.descricaoOriginal = res.descricaoOriginal || '';
                this.valorTotalDocumento = res.valorTotalDocumento || 0;
                this.qtdParcelas = res.qtdParcelas || 0;
                this.idCategoriaFinanceira = res.idCategoriaFinanceira || 0;
                this.nroParcela = res.nroParcela;
                this.statusDescricao = res.statusDescricao;
                this.podeEditarVencimento = res.podeEditarVencimento;

                // Liquidações
                this.liquidacoes = res.liquidacoes || [];

                // Anexos
                this.anexos = (res.anexos || []).map((a: any) => ({
                    idContasPagarParcelaAnexo: a.idContasPagarParcelaAnexo,
                    nomeArquivo: a.nomeArquivo || 'arquivo',
                    tamanhoBytes: a.tamanhoBytes || 0,
                    urlArquivo: a.urlArquivo
                }));

                // Popular formulário
                this.form.patchValue({
                    valor: res.valor,
                    dataVencimento: res.datetimeVencimento ? ContasPagarUtil.parseDateLocal(res.datetimeVencimento) : '',
                    datetimePrevisao: res.datetimePrevisao ? ContasPagarUtil.parseDateLocal(res.datetimePrevisao) : '',
                    descricao: res.descricao || '',
                    idFormaPagamento: res.idFormaPagamento,
                    idContaFinanceira: res.idContaFinanceira,
                    valorJurosPrevisto: res.valorJurosPrevisto || null,
                    valorMultaPrevisto: res.valorMultaPrevisto || null,
                    valorDescontoPrevisto: res.valorDescontoPrevisto || null,
                    valorAcrescimoPrevisto: res.valorAcrescimoPrevisto || null,
                    observacoesHtml: res.observacoesHtml || '',
                    codigoBarrasLinhaDigitavel: res.codigoBarrasLinhaDigitavel || '',
                    chavePix: res.chavePix || '',
                    tags: res.tags ?? []
                }, { emitEvent: false });

                // Rateio por centro de custo (pertence ao documento pai)
                this.form.get('dadosGerais.isRateio')?.setValue(res.hasRateio ?? false, { emitEvent: false });
                const itensArray = this.form.get('rateio.itens') as FormArray;
                itensArray.clear();
                if (res.hasRateio && res.rateios && res.rateios.length > 0) {
                    res.rateios.forEach((r: any) => {
                        itensArray.push(
                            this.fb.group({
                                idCentroCusto: [r.idCentroCusto, Validators.required],
                                valor: [r.valor, [Validators.min(0)]],
                                percentual: [r.percentual, [Validators.min(0), Validators.max(100)]],
                            })
                        );
                    });
                } else if (res.rateios && res.rateios.length === 1) {
                    // Centro de custo único (sem rateio): extrair do array de rateios
                    this.form.get('dadosGerais.idCentroCusto')?.setValue(res.rateios[0].idCentroCusto, { emitEvent: false });
                }

                // Desabilitar vencimento se tem pagamento parcial
                if (!this.podeEditarVencimento) {
                    this.form.get('dataVencimento')?.disable();
                }
            },
            error: () => {
                this.isLoading = false;
                this.alertService.show('Erro', 'Não foi possível carregar a parcela.', AlertType.error);
                this.router.navigate(['/financas/contas-pagar']);
            }
        });
    }

    public salvar(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            this.alertService.show('Atenção', 'Verifique os campos em vermelho.', AlertType.warning);
            return;
        }

        const f = this.form.getRawValue();

        if (f.dadosGerais.isRateio) {
            const itensRateio: any[] = f.rateio.itens || [];

            if (!itensRateio.length) {
                this.alertService.show('Atenção', 'Adicione ao menos uma linha de rateio por centro de custo.', AlertType.warning);
                return;
            }

            const possuiLinhaInvalida = itensRateio.some(
                item => !item.idCentroCusto || (!item.valor && !item.percentual)
            );
            if (possuiLinhaInvalida) {
                this.alertService.show('Atenção', 'Preencha centro de custo e valor ou percentual em todas as linhas do rateio.', AlertType.warning);
                return;
            }

            const somaPercentual = itensRateio.reduce((soma: number, item: any) => soma + (Number(item.percentual) || 0), 0);
            if (Math.abs(somaPercentual - 100) > 0.01) {
                this.alertService.show('Atenção', 'O rateio por centro de custo precisa totalizar 100%.', AlertType.warning);
                return;
            }
        }

        this.isSaving = true;

        const request: any = {
            idContasPagarParcela: this.idContasPagarParcela,
            valor: f.valor,
            dataVencimento: f.dataVencimento ? new Date(f.dataVencimento).toISOString() : null,
            datetimePrevisao: f.datetimePrevisao ? new Date(f.datetimePrevisao).toISOString() : null,
            descricao: f.descricao,
            idFormaPagamento: f.idFormaPagamento,
            idContaFinanceira: f.idContaFinanceira,
            valorJurosPrevisto: f.valorJurosPrevisto,
            valorMultaPrevisto: f.valorMultaPrevisto,
            valorDescontoPrevisto: f.valorDescontoPrevisto,
            valorAcrescimoPrevisto: f.valorAcrescimoPrevisto,
            observacoesHtml: f.observacoesHtml,
            codigoBarrasLinhaDigitavel: f.codigoBarrasLinhaDigitavel,
            chavePix: f.chavePix,
            tags: f.tags ?? [],
            hasRateio: f.dadosGerais.isRateio,
            rateios: f.dadosGerais.isRateio && f.rateio.itens.length > 0 ? f.rateio.itens : null,
            anexos: this.anexos.map(a => ({
                idContasPagarParcelaAnexo: a.idContasPagarParcelaAnexo || null,
                nomeArquivo: a.nomeArquivo,
                tamanhoBytes: a.tamanhoBytes,
                base64: a.base64 || null
            }))
        };

        this.contasPagarParcelaService.update(request).subscribe({
            next: (res) => {
                this.isSaving = false;
                if (res.isError) {
                    this.alertService.show('Erro', res.errorDescription || 'Erro ao atualizar a parcela.', AlertType.error);
                    return;
                }
                this.alertService.show('Sucesso', 'Parcela atualizada com sucesso!', AlertType.success);
                this.router.navigate(this.rotaVoltar);
            },
            error: () => {
                this.isSaving = false;
                this.alertService.show('Erro', 'Ocorreu um erro ao comunicar-se com o servidor.', AlertType.error);
            }
        });
    }

    public cancelar(): void {
        this.router.navigate(this.rotaVoltar);
    }

    // ─── Upload / Download ───────────────────────────────────────────────

    public toggleHover(event: boolean): void {
        this.isHovering = event;
    }

    public onDrop(event: DragEvent): void {
        event.preventDefault();
        this.isHovering = false;
        const files = event.dataTransfer?.files;
        if (files && files.length > 0) {
            this.processFiles(files);
        }
    }

    public onDragOver(event: DragEvent): void {
        event.preventDefault();
        this.isHovering = true;
    }

    public onDragLeave(event: DragEvent): void {
        event.preventDefault();
        this.isHovering = false;
    }

    public onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files && input.files.length > 0) {
            this.processFiles(input.files);
            input.value = '';
        }
    }

    private processFiles(files: FileList): void {
        const currentTotalSize = this.anexos.reduce((total, f) => total + f.tamanhoBytes, 0);
        let newFilesSize = 0;
        for (let i = 0; i < files.length; i++) {
            newFilesSize += files[i].size;
        }
        if (currentTotalSize + newFilesSize > this.MAX_TOTAL_SIZE) {
            this.alertService.show('Aviso', 'O tamanho total dos anexos não pode exceder 10MB.', AlertType.warning);
            return;
        }
        for (let i = 0; i < files.length; i++) {
            const file = files[i];
            if (file.size > this.MAX_FILE_SIZE) {
                this.alertService.show('Aviso', `O arquivo "${file.name}" excede o limite de 2MB.`, AlertType.warning);
                continue;
            }
            const reader = new FileReader();
            reader.onload = (e: any) => {
                const base64Data = e.target.result.split(',')[1];
                this.anexos.push({
                    nomeArquivo: file.name,
                    tamanhoBytes: file.size,
                    base64: base64Data
                });
            };
            reader.readAsDataURL(file);
        }
    }

    public removeAnexo(index: number): void {
        this.anexos.splice(index, 1);
    }

    public limparAnexos(): void {
        const dialogRef = this.dialog.open(ConfirmModalComponent, {
            data: {
                title: 'Confirmação',
                description: 'Deseja realmente remover todos os anexos?',
                buttonConfirm: 'Sim',
                buttonClose: 'Não'
            }
        });
        dialogRef.afterClosed().subscribe(result => {
            if (result && result.confirm) {
                this.anexos = [];
            }
        });
    }

    public formatBytes(bytes: number, decimals: number = 2): string {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
    }

    public downloadAnexo(index: number): void {
        const anexo = this.anexos[index];
        if (!anexo.urlArquivo) {
            this.alertService.show('Aviso', 'Este arquivo ainda não foi salvo no servidor.', AlertType.warning);
            return;
        }
        this.contasPagarParcelaService.getArquivoContasPagarParcela(anexo.urlArquivo).subscribe({
            next: (res: any) => {
                if (res.isError) {
                    this.alertService.show('Erro', res.errorDescription || 'Erro ao baixar o arquivo.', AlertType.error);
                    return;
                }
                const a = document.createElement('a');
                a.href = res.urlArquivo;
                a.download = anexo.nomeArquivo;
                a.target = '_blank';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
            },
            error: () => {
                this.alertService.show('Erro', 'Erro ao baixar o arquivo.', AlertType.error);
            }
        });
    }

    onMonthSelected(date: Date, datepicker: MatDatepicker<Date>, control: AbstractControl | null): void {
        if (!this.isContaCartao || !control) return;
        const dia = this.diaVencimentoCartao;
        const adjusted = dia != null ? ContasPagarUtil.ajustarParaDiaVencimento(date, dia) : date;
        control.setValue(adjusted);
        datepicker.close();
    }

    onDateInput(event: Event, controlName: string): void {
        // Sem o control, o texto digitado só ficava formatado visualmente (dd/mm/aaaa) mas nunca era
        // aplicado ao FormControl — quem "vencia" era o parser nativo do Angular Material (locale
        // errado), causando a dinâmica de digitação estranha relatada no bug.
        MaskService.onDateInput(event, this.form.get(controlName));
    }

}
